import { TabItem } from '../../../components/v2/ui/Tabs/tabs.types';
import LangEnum from '../../../lang/LangEnum';
import {
  ACTIONS_TAB_ID,
  ALL_TAB_ID,
  ENTITY_TAB_ORDER,
  SHORTCUTS_TAB_ID,
} from '../globalSearch.constants';
import { SearchResultGroup, SearchResultItem } from '../globalSearch.types';
import { formatResultCount } from '../lib/formatResultCount';
import { getEntityTabLabel } from '../lib/getEntityTabLabel';
import { StaticCommand } from '../staticCommands/staticCommands.types';

interface UseGlobalSearchTabsParams {
  groups: SearchResultGroup[];
  isStore: boolean;
  commands: StaticCommand[];
}

const orderGroups = (groups: SearchResultGroup[]): SearchResultGroup[] =>
  ENTITY_TAB_ORDER.map((entityType) =>
    groups.find((group) => group.entityType === entityType),
  ).filter((group): group is SearchResultGroup => Boolean(group));

const rankAllItems = (orderedGroups: SearchResultGroup[]): SearchResultItem[] =>
  orderedGroups
    .flatMap((group) => group.items)
    .sort(
      (a, b) =>
        b.score - a.score ||
        ENTITY_TAB_ORDER.indexOf(a.entityType) - ENTITY_TAB_ORDER.indexOf(b.entityType),
    );

const useGlobalSearchTabs = ({ groups, isStore, commands }: UseGlobalSearchTabsParams) => {
  const orderedGroups = orderGroups(groups);
  const allItems = rankAllItems(orderedGroups);

  const actionCommands = commands.filter((command) => command.type === 'action');
  const shortcutCommands = commands.filter((command) => command.type === 'shortcut');

  const entityTabs: TabItem[] = orderedGroups.map((group) => ({
    id: group.entityType,
    label: getEntityTabLabel({ entityType: group.entityType, isStore }),
    secondaryLabel: formatResultCount(group.total),
    testId: `global-search-tab-${group.entityType}`,
  }));

  const commandTabs: TabItem[] = [];
  if (actionCommands.length > 0) {
    commandTabs.push({
      id: ACTIONS_TAB_ID,
      label: LangEnum.GLOBAL_SEARCH_TAB_ACTIONS,
      secondaryLabel: actionCommands.length,
      testId: 'global-search-tab-actions',
    });
  }
  if (shortcutCommands.length > 0) {
    commandTabs.push({
      id: SHORTCUTS_TAB_ID,
      label: LangEnum.GLOBAL_SEARCH_TAB_SHORTCUTS,
      secondaryLabel: shortcutCommands.length,
      testId: 'global-search-tab-shortcuts',
    });
  }

  const hasAllTab = orderedGroups.length + commandTabs.length > 1;

  const allTotal = orderedGroups.reduce((sum, group) => sum + group.total, 0) + commands.length;

  const allTab: TabItem = {
    id: ALL_TAB_ID,
    label: LangEnum.GLOBAL_SEARCH_TAB_ALL,
    secondaryLabel: formatResultCount(allTotal),
    testId: 'global-search-tab-all',
  };

  const tabs = hasAllTab
    ? [allTab, ...entityTabs, ...commandTabs]
    : [...entityTabs, ...commandTabs];
  const defaultTabId = tabs[0]?.id;

  return { tabs, orderedGroups, allItems, defaultTabId };
};

export default useGlobalSearchTabs;
